import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { ConfigurationService } from "../configuration/configuration.service";
import {
  EligibilityGrouping,
  EligibilityQuestions,
  EligibilityStatus,
} from "./../../pages/patient-chart/eligibility/eligibility-questions.d";

@Injectable()
export class PalliativeService {
  baseUrl: string;
  chartUrl: string;
  headers: HttpHeaders;

  public readonly eligible = "ELIGIBLE";
  public readonly ineligible = "NOT_ELIGIBLE";
  public readonly disenrolled = "DISENROLLED";

  private readonly palliativeGroupingQuestions: string = "general";
  private readonly diagnosisGroupingQuestions: string = "specific";
  private readonly type = "palliative";

  private allQuestions: Array<EligibilityQuestions>;
  private palliativeQuestions: EligibilityQuestions;

  private questionsSubject: BehaviorSubject<EligibilityQuestions> =
    new BehaviorSubject(null);
  public questions$: Observable<EligibilityQuestions> =
    this.questionsSubject.asObservable();

  private needsDiagnosis = false;

  constructor(private http: HttpClient, private config: ConfigurationService) {
    this.baseUrl = config.getConfig().classificationUrl;
  }

  getQuestions(patientId: number): Observable<Array<EligibilityQuestions>> {
    return this.http
      .get<Array<EligibilityQuestions>>(
        `${this.baseUrl}/patient-charts/${patientId}/classifications`
      )
      .pipe(
        tap(
          (questions) => {
            if (!questions) return;
            this.allQuestions = questions;
            this.palliativeQuestions = questions.find(
              (q) => q.type === this.type
            );
            this.palliativeQuestions["patientId"] = patientId;
            this.questionsSubject.next(this.palliativeQuestions);
          },
          (error: HttpErrorResponse) => {
            if (error.status >= 500) {
              console.error(error);
            }

            if (error.status === 400 || error.status === 404) {
              this.needsDiagnosis = true;
            }
          }
        )
      );
  }

  getPalliativeQuestions(): EligibilityGrouping {
    return this.palliativeQuestions.groupings.find(
      (q) => q.grouping === this.palliativeGroupingQuestions
    );
  }

  getDiagnosisQuestions(): EligibilityGrouping {
    return this.palliativeQuestions.groupings.find(
      (q) => q.grouping === this.diagnosisGroupingQuestions
    );
  }

  getCompletedDate(): string {
    return this.palliativeQuestions.eligibilityCompleted;
  }

  isEligible(): boolean {
    return this.palliativeQuestions.eligibilityStatus.status ===
      this.ineligible ||
      this.palliativeQuestions.eligibilityStatus.status === this.disenrolled
      ? false
      : true;
  }

  isDisenrolled(): boolean {
    return (
      this.palliativeQuestions.eligibilityStatus.status === this.disenrolled
    );
  }

  getNeedsDiagnosis(): boolean {
    return this.needsDiagnosis;
  }

  getEligibility(): EligibilityStatus {
    return this.palliativeQuestions.eligibilityStatus;
  }

  saveQuestions(patientId: number): Observable<EligibilityQuestions> {
    delete this.palliativeQuestions["patientId"];
    return this.http.put<EligibilityQuestions>(
      `${this.baseUrl}/patient-charts/${patientId}/classifications/${this.palliativeQuestions.id}`,
      this.palliativeQuestions
    );
  }

  disenroll(patientId: number, reasons: Array<string>): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/patient-charts/${patientId}/classifications/${this.palliativeQuestions.id}/disenroll`,
      {
        status: "DISENROLLED",
        disenrollmentReasons: reasons,
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/patient-charts/palliative-service.ts